import React, { FC, useContext, useState } from 'react';
import { BBox, Feature, Polygon } from 'geojson';
import bboxPolygon from '@turf/bbox-polygon';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import booleanContains from '@turf/boolean-contains';
import { point } from '@turf/helpers';
import { DispatchContext, StateContext } from '../state/contexts';
import './filters.scss';
import MapsView from './components/maps-view';
import MapsConsent, { MapsConsentProperties } from './components/maps-consent';
import filterBy from './services/filter-by';
import Job from '../jobs/types/job';
import useFilterSetup from './hooks/use-filter-setup';
import OverlayJobList, {
  OverlayJobListItem,
  OverlayJobListItemProperties,
  OverlayJobListProperties,
} from './components/overlay-job-list';
import MarkerIcon, { MarkerIconProperties } from './components/marker-icon';

export type MapsFilterProperties = {
  overlayListClassName?: string;
  overlayItemClassName?: string;
  List?: React.ComponentType<OverlayJobListProperties>;
  Item?: React.ComponentType<OverlayJobListItemProperties>;
  Icon?: React.ComponentType<MarkerIconProperties>;
  Consent?: React.ComponentType<MapsConsentProperties>;
};

const createBoundsPolygon = (selected: string[]): Feature<Polygon, any> | undefined => {
  if (selected.length !== 4) {
    return undefined;
  }
  const bounds = selected.map((it) => Number(it)) as BBox2d;
  return bboxPolygon(bounds);
};

const filter = (jobs: Job[], selected: string[]): Job[] => {
  const boundsPolygon = createBoundsPolygon(selected);
  return typeof boundsPolygon !== 'undefined'
    ? jobs.filter((job) => {
        const locations = job.jobOpening.locations.map((location) =>
          typeof location.latitude === 'number' && typeof location.longitude === 'number'
            ? point([location.longitude!, location.latitude!])
            : undefined,
        );
        return locations.some(
          (location) => typeof location === 'undefined' || booleanContains(boundsPolygon, location),
        );
      })
    : jobs;
};

const filterJobLocations = (jobs: Job[], selected: string[]): Job[] => {
  const boundsPolygon = createBoundsPolygon(selected);
  return jobs
    .map((job) => {
      return {
        ...job,
        jobOpening: {
          ...job.jobOpening,
          locations: job.jobOpening.locations.filter(
            (location) =>
              typeof location.latitude === 'number' &&
              typeof location.longitude === 'number' &&
              (!boundsPolygon || booleanContains(boundsPolygon, point([location.longitude!, location.latitude!]))),
          ),
        },
      };
    })
    .filter(({ jobOpening }) => jobOpening.locations.length > 0);
};

const MapsFilter: FC<MapsFilterProperties> = ({
  overlayListClassName,
  overlayItemClassName,
  List = OverlayJobList,
  Item = OverlayJobListItem,
  Icon = MarkerIcon,
  Consent = MapsConsent,
}) => {
  const state = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const { config, data, jobs, filters } = state;
  const { maps: filterConfig } = config.config.filters;
  const { enabled, hidden, order } = filterConfig;
  const { language } = config.config.data;
  const { maps: mapsState } = filters;
  const { apiKey, styles, defaultCenter, requireConsent, privacyPolicyUrl } = config.config.api.maps;

  const [consentGiven, setConsentGiven] = useState(false);

  useFilterSetup('maps', filter);

  const publish = (bounds?: BBox) => {
    const selected = typeof bounds !== 'undefined' ? bounds.map((it) => String(it)) : [];
    dispatch(filterBy('maps', selected));
  };

  const jobsWithLocations = filterJobLocations(jobs.filtered, mapsState.selected);
  const loaded = config.type === 'themed' && data.type === 'processed';

  return enabled && !hidden && loaded ? (
    consentGiven || !requireConsent ? (
      <MapsView
        language={language}
        googleMapsApiKey={apiKey}
        styles={styles}
        defaultCenter={defaultCenter}
        jobs={jobsWithLocations}
        onChange={(bounds) => publish(bounds)}
        overlayListClassName={overlayListClassName}
        overlayItemClassName={overlayItemClassName}
        List={List}
        Item={Item}
        Icon={Icon}
        order={order}
      />
    ) : (
      <Consent privacyPolicyUrl={privacyPolicyUrl!} consentGiven={() => setConsentGiven(true)} />
    )
  ) : (
    <></>
  );
};

export default MapsFilter;
